/**
 * 資料エディタ 左パネル — 「基本情報」「AI 生成」の 2 タブ。
 * - 基本情報: タイトル / slug / パスワード / 自動返信文面 等のメタ編集
 * - AI 生成: プロンプト + 参照 URL + キーワードから sections を一括生成
 * blur 時に upsertDocument / generateDocument を呼び出して保存。
 */
import type { DocumentStatus, DocumentTheme } from '@goenhub/shared';
import { FileText, Loader2, Sparkles, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import {
  useGenerateDocument,
  useUpsertDocument,
  type DocumentRecord,
} from '@/hooks/useDocuments';

import {
  DOCUMENT_THEMES,
  THEME_DESCRIPTION,
  THEME_LABEL,
  THEME_SWATCH,
} from '../themes';

interface Props {
  document: DocumentRecord;
}

export function DocumentBriefPanel({ document }: Props) {
  return (
    <Tabs defaultValue="basic" className="p-3">
      <TabsList className="w-full">
        <TabsTrigger value="basic" className="flex-1 text-xs">
          基本情報
        </TabsTrigger>
        <TabsTrigger value="ai" className="flex-1 text-xs">
          AI 生成
        </TabsTrigger>
      </TabsList>

      <TabsContent value="basic" className="mt-3">
        <BasicInfoTab document={document} />
      </TabsContent>

      <TabsContent value="ai" className="mt-3">
        <AIGenerateTab document={document} />
      </TabsContent>
    </Tabs>
  );
}

function BasicInfoTab({ document }: Props) {
  const [title, setTitle] = useState(document.title);
  const [slug, setSlug] = useState(document.slug);
  const [status, setStatus] = useState<DocumentStatus>(document.status);
  const [theme, setTheme] = useState<DocumentTheme>(document.theme ?? 'light');
  const [password, setPassword] = useState('');
  const [resetPassword, setResetPassword] = useState(false);
  const [autoReplyTemplate, setAutoReplyTemplate] = useState(
    document.autoReplyTemplate ?? '',
  );
  // v3.2.24+: 認証方式
  const initialAuthMode: 'open' | 'password' | 'token' =
    document.authMode === 'open' ||
    document.authMode === 'password' ||
    document.authMode === 'token'
      ? document.authMode
      : document.passwordHash
        ? 'password'
        : 'open';
  const [authMode, setAuthMode] = useState<'open' | 'password' | 'token'>(
    initialAuthMode,
  );
  const [tokenValidityHours, setTokenValidityHours] = useState<string>(
    String(document.tokenValidityHours ?? 168),
  );
  // IME (日本語入力) 中の外部 value 同期スキップ用。
  // composition 中に refetch が走って setTitle 等で state リセットされると
  // input value prop が変わり IME が壊れ・カーソル末尾飛びの原因になる。
  const composingRef = useRef(false);
  const upsert = useUpsertDocument();

  useEffect(() => {
    if (composingRef.current) return;
    setTitle(document.title);
    setSlug(document.slug);
    setStatus(document.status);
    setTheme(document.theme ?? 'light');
    setAutoReplyTemplate(document.autoReplyTemplate ?? '');
  }, [
    document.id,
    document.title,
    document.slug,
    document.status,
    document.theme,
    document.autoReplyTemplate,
  ]);

  const persist = async (patch: {
    title?: string;
    slug?: string;
    status?: DocumentStatus;
    theme?: DocumentTheme;
    password?: string | null;
    autoReplyTemplate?: string | null;
    authMode?: 'open' | 'password' | 'token';
    tokenValidityHours?: number | null;
    visibleToSa?: boolean;
  }) => {
    try {
      const nextAuthMode = patch.authMode ?? authMode;
      const nextTokenHoursStr =
        patch.tokenValidityHours !== undefined
          ? patch.tokenValidityHours
          : Number(tokenValidityHours);
      const nextTokenHours =
        typeof nextTokenHoursStr === 'number' &&
        Number.isFinite(nextTokenHoursStr) &&
        nextTokenHoursStr > 0
          ? Math.floor(nextTokenHoursStr)
          : null;
      await upsert.mutateAsync({
        id: document.id,
        title: patch.title ?? title,
        slug: patch.slug ?? slug,
        status: patch.status ?? status,
        theme: patch.theme ?? theme,
        sections: document.sections ?? [],
        password: patch.password === undefined ? undefined : patch.password,
        autoReplyTemplate:
          patch.autoReplyTemplate === undefined
            ? autoReplyTemplate || null
            : patch.autoReplyTemplate,
        authMode: nextAuthMode,
        tokenValidityHours: nextTokenHours,
        visibleToSa: patch.visibleToSa ?? document.visibleToSa !== false,
      });
    } catch (e) {
      toast.error(`保存失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <div className="space-y-4 text-xs">
      <div>
        <div className="text-sm font-semibold mb-1">基本情報</div>
        <p className="text-[11px] text-[var(--text-muted)]">
          タイトル・slug・公開状態・パスワードなどの設定。
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="doc-title">タイトル</Label>
        <Input
          id="doc-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onCompositionStart={() => (composingRef.current = true)}
          onCompositionEnd={() => (composingRef.current = false)}
          onBlur={() => {
            if (composingRef.current) return;
            if (title !== document.title) void persist({ title });
          }}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="doc-slug">slug (英数 + ハイフン)</Label>
        <Input
          id="doc-slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          onCompositionStart={() => (composingRef.current = true)}
          onCompositionEnd={() => (composingRef.current = false)}
          onBlur={() => {
            if (composingRef.current) return;
            if (slug !== document.slug) void persist({ slug });
          }}
          placeholder="自動生成は空欄でOK"
          className="font-mono"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="doc-status">状態</Label>
        <select
          id="doc-status"
          value={status}
          onChange={(e) => {
            const next = e.target.value as DocumentStatus;
            setStatus(next);
            void persist({ status: next });
          }}
          className="w-full px-3 py-2 text-sm bg-[var(--bg-input)] border border-[var(--border)] rounded-sm"
        >
          <option value="draft">下書き</option>
          <option value="published">公開中</option>
          <option value="archived">アーカイブ</option>
        </select>
      </div>

      <div className="space-y-1.5">
        <Label>デザインテーマ</Label>
        <div className="grid grid-cols-1 gap-1.5">
          {DOCUMENT_THEMES.map((t) => {
            const swatch = THEME_SWATCH[t];
            const isActive = t === theme;
            return (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setTheme(t);
                  void persist({ theme: t });
                }}
                className={`flex items-center gap-2 p-2 rounded border text-left transition-colors ${
                  isActive
                    ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                    : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
                }`}
                aria-pressed={isActive}
              >
                <span
                  className="size-7 rounded border border-[var(--border)] flex-shrink-0 relative overflow-hidden"
                  style={{ background: swatch.bg }}
                >
                  <span
                    className="absolute right-1 bottom-1 size-3 rounded-full"
                    style={{ background: swatch.accent }}
                  />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="text-[11px] font-semibold block">
                    {THEME_LABEL[t]}
                  </span>
                  <span className="text-[10px] text-[var(--text-muted)] block leading-tight line-clamp-2">
                    {THEME_DESCRIPTION[t]}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="doc-authmode">閲覧認証方式 (v3.2.24+)</Label>
        <select
          id="doc-authmode"
          value={authMode}
          onChange={(e) => {
            const next = e.target.value as 'open' | 'password' | 'token';
            setAuthMode(next);
            void persist({ authMode: next });
          }}
          className="w-full px-3 py-2 text-sm bg-[var(--bg-input)] border border-[var(--border)] rounded-sm"
        >
          <option value="open">認証なし — URL を知っていれば誰でも閲覧</option>
          <option value="password">パスワード — 入力欄で照合、メールに平文掲載</option>
          <option value="token">ワンタイムトークン — リンクに自動付与、期限切れで無効</option>
        </select>
        {authMode === 'token' && (
          <div className="mt-1 flex items-center gap-2">
            <Label htmlFor="doc-token-hours" className="text-[11px] mb-0">
              リンク有効時間
            </Label>
            <Input
              id="doc-token-hours"
              type="number"
              inputMode="numeric"
              min={1}
              max={8760}
              value={tokenValidityHours}
              onChange={(e) => setTokenValidityHours(e.target.value)}
              onBlur={() => {
                const n = Number(tokenValidityHours);
                if (Number.isFinite(n) && n > 0) {
                  void persist({ tokenValidityHours: Math.floor(n) });
                }
              }}
              className="max-w-[100px]"
            />
            <span className="text-[11px] text-[var(--text-muted)]">時間 (例: 168 = 7 日)</span>
          </div>
        )}
        <p className="text-[10px] text-[var(--text-muted)]">
          {authMode === 'open' &&
            '※ 認証なし: URL が漏れたら誰でも閲覧可能です。'}
          {authMode === 'password' &&
            '※ パスワード: 自動返信メールには平文パスワードが挿入されます。既存のパスワード設定済み資料は、再入力すると平文も記録されます。'}
          {authMode === 'token' &&
            '※ ワンタイムトークン: 自動返信メールごとに固有のリンクが発行され、期限内なら何度でもアクセス可能。期限切れで無効化。'}
        </p>
      </div>

      {authMode === 'password' && (
        <div className="space-y-1.5">
          <Label htmlFor="doc-password">
            閲覧パスワード
            {document.passwordHash && !resetPassword && (
              <span className="ml-2 text-[10px] text-[var(--accent-green)]">
                · 設定済 ({document.passwordHint ?? '保護あり'})
                {document.passwordPlaintext
                  ? ' (メール本文に挿入される)'
                  : ' (平文未登録 — 再入力でメール挿入有効化)'}
              </span>
            )}
          </Label>
          {document.passwordHash && !resetPassword ? (
            <button
              type="button"
              onClick={() => setResetPassword(true)}
              className="text-[11px] text-[var(--accent)] hover:underline"
            >
              パスワードを変更／解除
            </button>
          ) : (
            <>
              <Input
                id="doc-password"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onCompositionStart={() => (composingRef.current = true)}
                onCompositionEnd={() => (composingRef.current = false)}
                onBlur={() => {
                  if (composingRef.current) return;
                  void persist({ password: password ? password : '' });
                  setPassword('');
                  setResetPassword(false);
                }}
                placeholder="例: 1234, caudesign-2026 (空欄で解除)"
              />
              <p className="text-[10px] text-[var(--text-muted)]">
                空欄で blur すると認証なし扱い。入力したパスワードは Firestore に
                平文でも保存され、自動返信メール本文に挿入されます。
              </p>
            </>
          )}
        </div>
      )}

      {/* v3.2.28+: SA ポータル表示可否 */}
      <div className="pt-2 border-t border-[var(--border)]">
        <label className="flex items-center justify-between gap-3 cursor-pointer select-none">
          <div className="flex-1">
            <div className="text-[12px] font-medium">SA ポータルに表示</div>
            <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
              OFF にすると外部 SA の資料集 (/sa/documents) で非表示になります。
            </div>
          </div>
          <input
            type="checkbox"
            checked={document.visibleToSa !== false}
            onChange={(e) => void persist({ visibleToSa: e.target.checked })}
            className="appearance-none relative w-10 h-5 rounded-full bg-[var(--bg-hover)] cursor-pointer transition-colors checked:bg-[var(--accent)] before:content-[''] before:absolute before:left-0.5 before:top-0.5 before:size-4 before:rounded-full before:bg-white before:transition-transform checked:before:translate-x-5"
          />
        </label>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="doc-autoreply">自動返信メールの追加文面 (任意)</Label>
        <Textarea
          id="doc-autoreply"
          value={autoReplyTemplate}
          onChange={(e) => setAutoReplyTemplate(e.target.value)}
          onCompositionStart={() => (composingRef.current = true)}
          onCompositionEnd={() => (composingRef.current = false)}
          onBlur={() => {
            if (composingRef.current) return;
            if ((autoReplyTemplate || '') !== (document.autoReplyTemplate ?? '')) {
              void persist({ autoReplyTemplate: autoReplyTemplate || null });
            }
          }}
          rows={4}
        />
      </div>

      {upsert.isPending && (
        <div className="text-[11px] text-[var(--text-muted)] flex items-center gap-1">
          <Loader2 className="size-3 animate-spin" />
          保存中…
        </div>
      )}
    </div>
  );
}

const MAX_PDF_BYTES = 8 * 1024 * 1024; // 8MB — Callable 10MB 制限を考慮

function AIGenerateTab({ document }: Props) {
  const [prompt, setPrompt] = useState('');
  const [url, setUrl] = useState('');
  const [keywords, setKeywords] = useState('');
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<string>('');
  const [pdfSize, setPdfSize] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const generate = useGenerateDocument();

  const hasSections = (document.sections?.length ?? 0) > 0;

  const handlePickPdf = async (file: File | null | undefined) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) {
      toast.error('PDF ファイル (.pdf) を選択してください');
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      toast.error(`PDF は ${Math.floor(MAX_PDF_BYTES / 1024 / 1024)}MB 以下にしてください`);
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      setPdfBase64(base64);
      setPdfName(file.name);
      setPdfSize(file.size);
    } catch (e) {
      toast.error(`PDF 読み込み失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleClearPdf = () => {
    setPdfBase64(null);
    setPdfName('');
    setPdfSize(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('プロンプトを入力してください');
      return;
    }
    if (url.trim() && !/^https?:\/\//i.test(url.trim())) {
      toast.error('URL は http:// または https:// で始めてください');
      return;
    }
    if (
      hasSections &&
      !confirm(
        '既存のセクションは AI 生成結果で上書きされます。続行しますか？',
      )
    )
      return;
    try {
      const res = await generate.mutateAsync({
        documentId: document.id,
        prompt: prompt.trim(),
        referenceUrl: url.trim() || undefined,
        referenceKeywords: keywords.trim() || undefined,
        pdfBase64: pdfBase64 || undefined,
        pdfName: pdfBase64 ? pdfName : undefined,
      });
      toast.success(`資料を生成しました (${res.sectionCount} セクション)`);
    } catch (e) {
      toast.error(`生成失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <div className="space-y-4 text-xs">
      <div>
        <div className="text-sm font-semibold mb-1">AI 生成</div>
        <p className="text-[11px] text-[var(--text-muted)]">
          プロンプトと参照 URL を元に資料のセクション構成を一括で生成します。
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ai-prompt">プロンプト (長文可)</Label>
        <Textarea
          id="ai-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={8}
          placeholder={
            '例: 製造業の経営者向けに、自社の AI 検査装置「GH-2000」の提案資料を作成。\n対象は中小製造業の品質管理部門。\n価格帯・導入実績・他社比較・FAQ を含めてください。'
          }
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ai-url">参照 URL (任意)</Label>
        <Input
          id="ai-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/product"
          className="font-mono"
        />
        <p className="text-[10px] text-[var(--text-muted)]">
          指定するとサーバが該当ページを取得し、本文を参考にして生成します。
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ai-keywords">補足キーワード (任意)</Label>
        <Input
          id="ai-keywords"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="例: 価格, 導入事例, 仕様"
        />
        <p className="text-[10px] text-[var(--text-muted)]">
          カンマ区切り。URL / PDF からこのキーワードを含む段落を優先抽出します。
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ai-pdf">参照 PDF (任意)</Label>
        {pdfBase64 ? (
          <div className="flex items-center gap-2 p-2 border border-[var(--border)] rounded-md bg-[var(--bg-elevated)]">
            <FileText className="size-4 text-[var(--accent)] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] truncate" title={pdfName}>
                {pdfName}
              </div>
              <div className="text-[10px] text-[var(--text-muted)]">
                {(pdfSize / 1024).toFixed(1)} KB
              </div>
            </div>
            <button
              type="button"
              onClick={handleClearPdf}
              className="text-[var(--text-muted)] hover:text-red-500"
              title="PDF を解除"
            >
              <X className="size-3.5" />
            </button>
          </div>
        ) : (
          <>
            <input
              ref={fileInputRef}
              id="ai-pdf"
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => void handlePickPdf(e.target.files?.[0])}
              className="block w-full text-[11px] file:mr-2 file:py-1 file:px-2 file:rounded-sm file:border file:border-[var(--border)] file:bg-[var(--bg-elevated)] file:text-[11px] file:cursor-pointer hover:file:bg-[var(--bg-hover)]"
            />
            <p className="text-[10px] text-[var(--text-muted)]">
              最大 {Math.floor(MAX_PDF_BYTES / 1024 / 1024)}MB。サーバが PDF からテキスト抽出して参照します。
            </p>
          </>
        )}
      </div>

      <Button
        className="w-full"
        disabled={generate.isPending || !prompt.trim()}
        onClick={handleGenerate}
      >
        {generate.isPending ? (
          <>
            <Loader2 className="size-3.5 mr-1 animate-spin" />
            生成中... (最大 3 分)
          </>
        ) : (
          <>
            <Sparkles className="size-3.5 mr-1" />
            {hasSections ? '資料を再生成' : '資料を生成'}
          </>
        )}
      </Button>

      {hasSections && (
        <p className="text-[10px] text-[var(--text-muted)] text-center">
          ※ 既存セクションは生成結果で上書きされます
        </p>
      )}
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('読み込み結果が文字列ではありません'));
        return;
      }
      // result は "data:application/pdf;base64,...." の形なので prefix を剥がす
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('読み込み失敗'));
    reader.readAsDataURL(file);
  });
}
